import { ClassNamesConfig } from 'react-select';

import { i18n } from '@cccom/shared/i18n';
import { log } from '@cccom/shared/logging';
import { cn } from '@cccom/shared/ui';

export type SelectOption = {
  label: string;
  value: Record<string, unknown> | string | number;
};

export const isSelectOption = (x: any): x is SelectOption =>
  x?.label && x?.value;

/**
 * Transforms an array of strings into an array of SelectOption objects.
 *
 * @param options - Array of string values to be converted into select options
 * @param translationKey - Optional i18n translation key prefix. When provided,
 *                         the function converts each option to lowercase and attempts
 *                         to find translations using the pattern
 *                         `${translationKey}.${option.toLowerCase()}`. If a translation
 *                         exists, it's used as the label; otherwise, the original option
 *                         value is used as both label and value.
 * @returns Array of SelectOption objects with label and value properties
 * @throws {Error} If the options parameter is not an array
 *
 * @example
 * // Without translation
 * mapSelectOptions(['red', 'blue', 'green'])
 * // Returns: [{ label: 'red', value: 'red' }, { label: 'blue', value: 'blue' }, ...]
 *
 * @example
 * // With translation (assuming i18n key 'colors.red' exists)
 * mapSelectOptions(['RED', 'BLUE'], 'colors')
 * // Returns: [{ label: 'Red Color', value: 'Red' }, ...]
 * // Note: Options are converted to lowercase for translation key matching
 */
export const mapSelectOptions = (
  options: string[] = [],
  translationKey?: string
) => {
  if (!Array.isArray(options)) {
    const errMsg =
      'Type Error: MapSelectOptions : Invalid argument for options parameter: %o options must be an array';
    log.error(errMsg, options);
    throw new Error(errMsg);
  }

  const optionsArr: SelectOption[] = options.map((option) => {
    const key = `${translationKey}.${option.toLowerCase()}`;
    if (translationKey && i18n.exists(key))
      return { label: i18n.t(key), value: option };

    return { label: option, value: option };
  });

  return optionsArr;
};

export const getSelectClassNames = (
  hasErrors?: boolean
): ClassNamesConfig<any, any, any> => ({
  control: ({ isFocused }: { isFocused: boolean }) =>
    cn(
      'flex !rounded-md !border-input bg-background px-1 text-sm shadow-sm transition-colors',
      'disabled:cursor-not-allowed disabled:opacity-50',
      isFocused && '!ring-1 !ring-ring',
      hasErrors && !isFocused && '!border-red-500'
    ),
  placeholder: () => '!text-gray-400',
  input: () => 'text-sm',
  menuList: () => '!border-2 !border-gray-200 !rounded-md',
  option: ({
    isFocused,
    isSelected,
  }: {
    isFocused: boolean;
    isSelected: boolean;
  }) =>
    cn(
      isFocused && '!bg-accent text-accent-foreground',
      isSelected && '!bg-brand-500 !text-white'
    ),
  multiValueRemove: () => 'hover:!bg-gray-300 hover:!text-gray-500',
  indicatorSeparator: () => 'hidden',
});
